import { memo, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import {
  splitByHighlights,
  type Block,
  type Highlight,
  type Inline,
} from "@/lib/prompt-markdown"
import type { PromptPart } from "@/lib/prompt-audit-api"

export type ReaderMode = "rendered" | "source"

type ReaderProps = {
  text: string
  blocks: Block[]
  mode: ReaderMode
  highlights: Highlight[]
  activeThread: string | null
  parts: PromptPart[]
  onHighlightClick: (threadId: string) => void
}

type Context = Pick<ReaderProps, "text" | "highlights" | "activeThread" | "onHighlightClick">

function Pieces({ ctx, start, end }: { ctx: Context; start: number; end: number }) {
  return (
    <>
      {splitByHighlights(start, end, ctx.highlights).map((piece) => {
        const content = ctx.text.slice(piece.start, piece.end)
        if (!piece.highlight) {
          return <span key={piece.start} data-s={piece.start} data-e={piece.end}>{content}</span>
        }
        const threadId = piece.highlight
        return (
          <mark
            key={piece.start}
            data-s={piece.start}
            data-e={piece.end}
            data-thread={threadId}
            role="button"
            tabIndex={0}
            aria-label={`Open comment on: ${content.slice(0, 60)}`}
            onClick={() => ctx.onHighlightClick(threadId)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                ctx.onHighlightClick(threadId)
              }
            }}
            className={cn(
              "cursor-pointer rounded-[2px] text-inherit transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
              ctx.activeThread === threadId
                ? "bg-amber-300/90 dark:bg-amber-400/45"
                : "bg-amber-200/70 hover:bg-amber-300/80 dark:bg-amber-400/20 dark:hover:bg-amber-400/35",
            )}
          >
            {content}
          </mark>
        )
      })}
    </>
  )
}

function Inlines({ ctx, inlines }: { ctx: Context; inlines: Inline[] }) {
  return (
    <>
      {inlines.map((inline) => {
        const body = <Pieces ctx={ctx} start={inline.start} end={inline.end} />
        if (inline.kind === "strong") return <strong key={inline.start} className="font-semibold text-foreground">{body}</strong>
        if (inline.kind === "code") {
          return (
            <code key={inline.start} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground">
              {body}
            </code>
          )
        }
        return <span key={inline.start}>{body}</span>
      })}
    </>
  )
}

const HEADING_STYLE: Record<number, string> = {
  1: "mt-10 text-2xl font-semibold tracking-tight",
  2: "mt-9 text-xl font-semibold tracking-tight",
  3: "mt-7 text-lg font-semibold",
  4: "mt-6 text-base font-semibold",
  5: "mt-5 text-sm font-semibold uppercase tracking-wide text-muted-foreground",
  6: "mt-5 text-sm font-semibold text-muted-foreground",
}

function BlockView({ ctx, block }: { ctx: Context; block: Block }) {
  const anchor = { "data-block-start": block.start }
  switch (block.type) {
    case "heading": {
      const Tag = `h${Math.min(block.level + 1, 6)}` as "h2"
      return (
        <Tag {...anchor} className={cn("scroll-mt-24 text-balance text-foreground first:mt-0", HEADING_STYLE[block.level])}>
          <Inlines ctx={ctx} inlines={block.inlines} />
        </Tag>
      )
    }
    case "paragraph":
      return (
        <p {...anchor} className="mt-4 text-pretty first:mt-0">
          <Inlines ctx={ctx} inlines={block.inlines} />
        </p>
      )
    case "list": {
      const Tag = block.ordered ? "ol" : "ul"
      return (
        <Tag {...anchor} className={cn("mt-4 space-y-1.5 pl-6 first:mt-0", block.ordered ? "list-decimal" : "list-disc")}>
          {block.items.map((item) => (
            <li
              key={item.start}
              value={block.ordered ? Number.parseInt(item.marker, 10) || undefined : undefined}
              style={{ marginLeft: `${Math.floor(item.indent / 2) * 1.25}rem` }}
              className="pl-1 marker:text-muted-foreground"
            >
              <Inlines ctx={ctx} inlines={item.inlines} />
            </li>
          ))}
        </Tag>
      )
    }
    case "code":
      return (
        <pre {...anchor} className="mt-4 overflow-x-auto rounded-lg border bg-muted/50 p-4 font-mono text-[13px] leading-relaxed first:mt-0">
          <Pieces ctx={ctx} start={block.bodyStart} end={block.bodyEnd} />
        </pre>
      )
    case "table":
      return (
        <pre {...anchor} className="mt-4 overflow-x-auto rounded-lg border p-4 font-mono text-[13px] leading-relaxed first:mt-0">
          <Pieces ctx={ctx} start={block.start} end={block.end} />
        </pre>
      )
    case "rule":
      return <hr {...anchor} className="my-8 border-border" />
  }
}

function PartDivider({ part }: { part: PromptPart }) {
  return (
    <div className="mt-12 mb-6 flex select-none items-center gap-3 first:mt-0" aria-hidden="true">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{part.label}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

function RenderedView({ ctx, blocks, parts }: { ctx: Context; blocks: Block[]; parts: PromptPart[] }) {
  const pending = [...parts].sort((a, b) => a.start - b.start)
  const nodes: ReactNode[] = []
  for (const block of blocks) {
    while (pending.length && pending[0].start <= block.start) {
      const part = pending.shift()!
      nodes.push(<PartDivider key={`part-${part.start}`} part={part} />)
    }
    nodes.push(<BlockView key={block.start} ctx={ctx} block={block} />)
  }
  return <>{nodes}</>
}

function sourceLines(text: string): Array<[number, number]> {
  const lines: Array<[number, number]> = []
  let start = 0
  while (start < text.length) {
    const newline = text.indexOf("\n", start)
    const end = newline < 0 ? text.length : newline + 1
    lines.push([start, end])
    start = end
  }
  return lines
}

export const PromptReader = memo(function PromptReader({
  text, blocks, mode, highlights, activeThread, parts, onHighlightClick,
}: ReaderProps) {
  const ctx: Context = { text, highlights, activeThread, onHighlightClick }
  if (!text) return <p className="text-sm text-muted-foreground">This prompt is empty in this process.</p>
  if (mode === "source") {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-foreground">
        {sourceLines(text).map(([start, end]) => (
          <span key={start} data-block-start={start} className="transition-colors">
            <Pieces ctx={ctx} start={start} end={end} />
          </span>
        ))}
      </pre>
    )
  }
  return (
    <div className="text-[15px] leading-[1.7] text-foreground/90">
      <RenderedView ctx={ctx} blocks={blocks} parts={parts} />
    </div>
  )
})
